import { authenticatedHttpClient } from './httpClient';

export interface CustomerCapabilityInfo {
  type: string;
  title: string;
  description?: string;
  write?: boolean;
}

export interface TelegramIntegration {
  enabled: boolean;
  botUsername?: string | null;
  botId?: string | null;
  managedBot?: boolean;
  webhookSetAt?: string | null;
  allowSelfRegister?: boolean;
  autoApproveSelfRegister?: boolean;
  requiredOnboardingFields?: string[];
  availableOnboardingFields?: { key: string; title: string; description?: string }[];
  greeting?: string | null;
  closedHoursReply?: string | null;
  deliveryNotes?: string | null;
  paymentNotes?: string | null;
  orgInstructions?: string | null;
  createDeepLink?: string | null;
  suggestedBotUsername?: string | null;
  /** Default true: customer catalog only includes products with stock. */
  onlyInStockProducts?: boolean;
  /** Default true: quantity buttons/custom qty capped by availability. */
  capQuantityToStock?: boolean;
  enabledCustomerActions?: string[];
  availableCustomerActions?: CustomerCapabilityInfo[];
}

export const getTelegramIntegration = (organizationId: string) =>
  authenticatedHttpClient.get<TelegramIntegration>(`/telegram/${organizationId}`);

export const prepareTelegramBot = (organizationId: string) =>
  authenticatedHttpClient.post<TelegramIntegration>(
    `/telegram/${organizationId}/prepare`,
    {}
  );

export const connectTelegramToken = (organizationId: string, botToken: string) =>
  authenticatedHttpClient.post<TelegramIntegration>(
    `/telegram/${organizationId}/connect`,
    { botToken }
  );

export const updateTelegramPolicy = (
  organizationId: string,
  payload: Partial<TelegramIntegration>
) =>
  authenticatedHttpClient.put<TelegramIntegration>(
    `/telegram/${organizationId}/policy`,
    payload
  );

export const disconnectTelegram = (organizationId: string) =>
  authenticatedHttpClient.post(`/telegram/${organizationId}/disconnect`, {});

export interface ChannelConversation {
  id: string;
  identityId: string;
  clientId?: string;
  clientName?: string;
  status?: string;
  lastPreview?: string;
  lastMessageAt?: string;
  orderCount?: number;
  clientOrderId?: string;
}

export interface ChannelMessage {
  id: string;
  role: string;
  content: string;
  created?: string;
}

export interface ChannelIdentity {
  id: string;
  telegramUsername?: string;
  displayName?: string;
  status: string;
  clientId?: string;
  clientName?: string;
  pendingNote?: string;
  pendingRif?: string;
  pendingBusinessName?: string;
  pendingExtras?: Record<string, string | null | undefined>;
}

export const listChannelConversations = (organizationId: string, status?: string) => {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  return authenticatedHttpClient.get<{
    conversations: ChannelConversation[];
    hasMore: boolean;
  }>(`/channel/${organizationId}/conversations${q}`);
};

export const listChannelMessages = (
  organizationId: string,
  conversationId: string
) =>
  authenticatedHttpClient.get<{ messages: ChannelMessage[] }>(
    `/channel/${organizationId}/conversations/${conversationId}/messages`
  );

export const listChannelIdentities = (organizationId: string, status?: string) => {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  return authenticatedHttpClient.get<{ identities: ChannelIdentity[] }>(
    `/channel/${organizationId}/identities${q}`
  );
};

export const linkChannelIdentity = (
  organizationId: string,
  telegramUserId: string,
  clientId: string
) =>
  authenticatedHttpClient.post(
    `/channel/${organizationId}/identities/${telegramUserId}/link`,
    { clientId }
  );

export const approveChannelIdentity = (
  organizationId: string,
  telegramUserId: string,
  code?: string
) =>
  authenticatedHttpClient.post(
    `/channel/${organizationId}/identities/${telegramUserId}/approve`,
    code?.trim() ? { code: code.trim() } : {}
  );

export const blockChannelIdentity = (
  organizationId: string,
  telegramUserId: string
) =>
  authenticatedHttpClient.post(
    `/channel/${organizationId}/identities/${telegramUserId}/block`,
    {}
  );

export const rejectChannelIdentity = (
  organizationId: string,
  telegramUserId: string,
  reason?: string
) =>
  authenticatedHttpClient.post(
    `/channel/${organizationId}/identities/${telegramUserId}/reject`,
    reason ? { reason } : {}
  );

export const unblockChannelIdentity = (
  organizationId: string,
  telegramUserId: string
) =>
  authenticatedHttpClient.post(
    `/channel/${organizationId}/identities/${telegramUserId}/unblock`,
    {}
  );

export const createChannelInvite = (organizationId: string, clientId: string) =>
  authenticatedHttpClient.post<{
    token: string;
    clientId: string;
    clientName?: string;
    url?: string;
  }>(`/channel/${organizationId}/invites`, { clientId });

export const testCustomerChat = (
  organizationId: string,
  message: string,
  history: { role: string; content: string }[],
  clientId?: string
) =>
  authenticatedHttpClient.post<{
    reply: string;
    actions: unknown[];
    creditsCharged: number;
    creditsRemaining?: number;
    timings?: Record<string, number> | null;
  }>(`/channel/${organizationId}/test-chat`, {
    message,
    history,
    clientId,
  });
